/**
 * Signal modal — intelligence signal detail inspection.
 *
 * Opens when a signal is clicked anywhere in the app (signal:inspect event).
 * Displays signal type, severity, confidence, contributing sources,
 * related signals in the same region, and a mini timeline.
 */

import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import type { AggregatedSignal } from '@/intelligence/analysis/types';
import type { NormalizedEvent } from '@/sources/base-source';
import { relativeTime, formatUTC } from '@/utils/time';
import { haversineDistance } from '@/utils/geo';
import { getAnalysisEngine } from '@/intelligence/analysis/engine';

export function createSignalModal(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'signal-modal';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];

  function renderEmpty(): void {
    contentArea.textContent = '';
    const msg = document.createElement('div');
    msg.style.cssText = 'padding: 32px 16px; text-align: center; color: var(--text-muted); font-size: 12px;';
    msg.textContent = 'Select a signal to inspect';
    contentArea.appendChild(msg);
  }

  function renderSectionHeader(text: string): HTMLElement {
    const header = document.createElement('div');
    header.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.05em;';
    header.textContent = text;
    return header;
  }

  function render(signal: AggregatedSignal): void {
    contentArea.textContent = '';

    // --- Header ---
    const header = document.createElement('div');
    header.style.cssText = 'margin-bottom: 12px;';

    const title = document.createElement('h3');
    title.style.cssText = 'margin: 0; font-size: 14px; font-weight: 600; color: var(--text-primary); margin-bottom: 4px;';
    title.textContent = `${signal.country} (${signal.countryCode})`;
    header.appendChild(title);

    if (signal.zone) {
      const zone = document.createElement('div');
      zone.style.cssText = 'font-size: 11px; color: var(--text-muted);';
      zone.textContent = `Convergence zone: ${signal.zone}`;
      header.appendChild(zone);
    }

    const convScore = document.createElement('div');
    convScore.style.cssText = 'font-size: 11px; color: var(--text-secondary); margin-top: 2px;';
    convScore.textContent = `Convergence score: ${signal.convergenceScore}`;
    header.appendChild(convScore);

    contentArea.appendChild(header);

    // --- Signal Types ---
    const signalsSection = document.createElement('div');
    signalsSection.style.cssText = 'margin-bottom: 16px;';
    signalsSection.appendChild(renderSectionHeader(`Signal Types (${signal.signals.length})`));

    for (const sig of signal.signals) {
      const row = document.createElement('div');
      row.style.cssText = `
        display: flex; align-items: center; gap: 8px; padding: 4px 8px;
        margin-bottom: 3px; border-radius: 4px; background: var(--bg-secondary);
      `;

      const sevColor = getSeverityColor(sig.severity);

      const dot = document.createElement('div');
      dot.style.cssText = `width: 6px; height: 6px; border-radius: 50%; background: ${sevColor}; flex-shrink: 0;`;
      row.appendChild(dot);

      const typeName = document.createElement('span');
      typeName.style.cssText = 'font-size: 11px; color: var(--text-primary); flex: 1;';
      typeName.textContent = sig.type.replace(/_/g, ' ');
      row.appendChild(typeName);

      const countBadge = document.createElement('span');
      countBadge.style.cssText = 'font-size: 10px; font-family: var(--font-mono); color: var(--text-muted);';
      countBadge.textContent = `\u00D7${sig.count}`;
      row.appendChild(countBadge);

      const sevBadge = document.createElement('span');
      sevBadge.style.cssText = `font-size: 9px; font-weight: 600; text-transform: uppercase; padding: 1px 5px; border-radius: 3px; background: ${sevColor}22; color: ${sevColor};`;
      sevBadge.textContent = sig.severity;
      row.appendChild(sevBadge);

      signalsSection.appendChild(row);
    }

    contentArea.appendChild(signalsSection);

    // --- Contributing Sources ---
    const sourcesSection = document.createElement('div');
    sourcesSection.style.cssText = 'margin-bottom: 16px;';
    sourcesSection.appendChild(renderSectionHeader('Contributing Events'));

    // Find events from this country
    const events = appStore.getState().events;
    const countryEvents = events.filter(e => {
      const cc = e.location?.countryCode?.toUpperCase();
      return cc === signal.countryCode.toUpperCase();
    });

    const recent = countryEvents
      .slice()
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 15);

    if (recent.length === 0) {
      const empty = document.createElement('div');
      empty.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic;';
      empty.textContent = 'No contributing events found';
      sourcesSection.appendChild(empty);
    } else {
      for (const ev of recent) {
        const item = document.createElement('div');
        item.style.cssText = `
          padding: 4px 8px; margin-bottom: 3px; border-radius: 4px;
          background: var(--bg-secondary); cursor: pointer;
        `;
        item.onclick = () => eventBus.emit('event:selected', ev);

        const itemTitle = document.createElement('div');
        itemTitle.style.cssText = 'font-size: 11px; color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;';
        itemTitle.textContent = ev.title;
        item.appendChild(itemTitle);

        const itemMeta = document.createElement('div');
        itemMeta.style.cssText = 'font-size: 10px; color: var(--text-muted);';
        itemMeta.textContent = `${ev.source} \u00B7 ${relativeTime(ev.timestamp)}`;
        item.appendChild(itemMeta);

        sourcesSection.appendChild(item);
      }
    }

    contentArea.appendChild(sourcesSection);

    // --- Related Signals ---
    const relatedSection = document.createElement('div');
    relatedSection.style.cssText = 'margin-bottom: 16px;';
    relatedSection.appendChild(renderSectionHeader('Related Signals'));

    const engine = getAnalysisEngine();
    const result = engine?.getLastResult();
    const allSignals = result?.aggregatedSignals ?? [];

    const related = allSignals.filter(s =>
      s.countryCode !== signal.countryCode &&
      hasNearbyEvents(signal, s, events),
    ).slice(0, 5);

    if (related.length === 0) {
      const empty = document.createElement('div');
      empty.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic;';
      empty.textContent = 'No related signals detected';
      relatedSection.appendChild(empty);
    } else {
      for (const rel of related) {
        const row = document.createElement('div');
        row.style.cssText = `
          padding: 4px 8px; margin-bottom: 3px; border-radius: 4px;
          background: var(--bg-secondary); cursor: pointer;
        `;
        row.onclick = () => eventBus.emit('signal:inspect', rel);

        const relName = document.createElement('span');
        relName.style.cssText = 'font-size: 11px; color: var(--text-primary);';
        relName.textContent = `${rel.country} (${rel.countryCode})`;
        row.appendChild(relName);

        const relScore = document.createElement('span');
        relScore.style.cssText = 'font-size: 10px; color: var(--text-muted); margin-left: 8px;';
        relScore.textContent = `conv ${rel.convergenceScore}`;
        row.appendChild(relScore);

        relatedSection.appendChild(row);
      }
    }

    contentArea.appendChild(relatedSection);

    // --- Timeline ---
    const timelineSection = document.createElement('div');
    timelineSection.appendChild(renderSectionHeader('Event Timeline'));

    if (recent.length > 0) {
      for (const ev of recent.slice(0, 10)) {
        const tRow = document.createElement('div');
        tRow.style.cssText = 'display: flex; align-items: flex-start; gap: 8px; margin-bottom: 4px;';

        const timeLabel = document.createElement('span');
        timeLabel.style.cssText = 'font-size: 10px; font-family: var(--font-mono); color: var(--text-muted); min-width: 50px; flex-shrink: 0;';
        timeLabel.textContent = formatUTC(ev.timestamp).slice(11, 16);
        tRow.appendChild(timeLabel);

        const dot = document.createElement('div');
        dot.style.cssText = `width: 4px; height: 4px; border-radius: 50%; background: var(--accent); margin-top: 5px; flex-shrink: 0;`;
        tRow.appendChild(dot);

        const desc = document.createElement('span');
        desc.style.cssText = 'font-size: 10px; color: var(--text-secondary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;';
        desc.textContent = `[${ev.source}] ${ev.title}`;
        tRow.appendChild(desc);

        timelineSection.appendChild(tRow);
      }
    }

    contentArea.appendChild(timelineSection);
  }

  // Initial empty state
  renderEmpty();

  // Listen for signal:inspect
  cleanupFns.push(
    eventBus.on('signal:inspect', (signal) => render(signal)),
  );

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}

function getSeverityColor(severity: string): string {
  switch (severity) {
    case 'critical': return 'var(--severity-critical)';
    case 'high': return 'var(--severity-high)';
    case 'medium': return 'var(--severity-medium)';
    case 'low': return 'var(--severity-low)';
    default: return 'var(--text-muted)';
  }
}

/** Check if two signals have events within 50km of each other. */
function hasNearbyEvents(
  a: AggregatedSignal,
  b: AggregatedSignal,
  events: NormalizedEvent[],
): boolean {
  // Quick check: find one event from each country and see if they're within 50km
  const aEvents = events.filter(e => e.location?.countryCode?.toUpperCase() === a.countryCode.toUpperCase() && e.location);
  const bEvents = events.filter(e => e.location?.countryCode?.toUpperCase() === b.countryCode.toUpperCase() && e.location);

  if (aEvents.length === 0 || bEvents.length === 0) return false;

  // Just check first event from each for performance
  const aLoc = aEvents[0].location!;
  const bLoc = bEvents[0].location!;
  return haversineDistance(aLoc.lat, aLoc.lng, bLoc.lat, bLoc.lng) <= 50;
}
